/**
 * The centre screen (2026-10-05, the user's reference image): one extra-large display in the
 * middle of the arc, facing the table. It opens on the study summary; the table console's
 * thumbnails project any dashboard onto it. A projected dashboard is a live clone at the
 * centre screen's scale with its own drill path, under the user's room filter, so it drills,
 * raises queries, pins and broadcasts like the panel on the arc. What it shows is shared
 * with the room (2026-10-06, `state` and `setState`), like every other screen.
 */
import * as THREE from 'three';
import { DASHBOARD_TITLES, type DashboardFilters, type DashboardId } from '@clinspace/shared';
import { cleanFilters, type Dashboard, type DashboardHost } from './Dashboard.ts';
import { createDashboard } from './index.ts';
import { StudySummary } from './StudySummary.ts';
import { CENTRE } from '../world/plan.ts';

export type CentreView = DashboardId | 'summary';

export interface CentrePose {
  position: THREE.Vector3;
  yaw: number;
  /** Over the dashboards' 2.4 x 1.53 m. */
  scale: number;
}

export class CentreScreen {
  readonly group = new THREE.Group();
  readonly summary: StudySummary;
  clone: Dashboard | undefined;
  showing: CentreView = 'summary';
  private filters: DashboardFilters = {};
  private visible = true;

  constructor(
    private readonly host: DashboardHost,
    private readonly pose: CentrePose,
    private readonly onChange: (view: CentreView) => void,
  ) {
    this.group.name = 'centre-screen';
    this.summary = new StudySummary(host.view, {
      project: (id) => this.show(id),
      tooltip: (lines, at) => host.tooltip(lines, at),
    });
    const mesh = this.summary.panel.mesh;
    mesh.position.copy(pose.position);
    mesh.rotation.y = pose.yaw;
    mesh.scale.setScalar(pose.scale);
    this.group.add(mesh);
    this.summary.panel.attach(host.interactive);
  }

  get title(): string {
    return this.showing === 'summary' ? 'Study summary' : DASHBOARD_TITLES[this.showing];
  }

  /** Puts the summary or a dashboard on the screen; showing the same one again resets it. */
  show(view: CentreView): void {
    if (view === this.showing) {
      if (this.clone?.path.length) this.clone.setPath([]);
      return;
    }
    this.dropClone();
    this.showing = view;
    const summaryMesh = this.summary.panel.mesh;
    if (view === 'summary') {
      summaryMesh.visible = this.visible;
      if (this.visible) this.summary.panel.attach(this.host.interactive);
    } else {
      summaryMesh.visible = false;
      this.summary.panel.detach(this.host.interactive);
      // Drawn as wide as the screen (2026-10-07): its charts and tables use the full 9 m.
      const clone = createDashboard(view, this.host, { wide: CENTRE.wide });
      // Sharper than the arc panels: it is read from the table, several metres away.
      clone.panel.setResolution(2);
      clone.filters = { ...this.filters };
      clone.home.position.copy(this.pose.position);
      clone.home.quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), this.pose.yaw);
      clone.home.scale = this.pose.scale;
      clone.goHome();
      clone.invalidate();
      clone.group.visible = this.visible;
      this.group.add(clone.group);
      if (this.visible) clone.attach();
      this.clone = clone;
    }
    this.onChange(view);
  }

  /** What the room shares: what is on the screen, how far it is drilled, its filters. */
  state(): { view: CentreView; path: string[]; filters: DashboardFilters } {
    return {
      view: this.showing,
      path: [...(this.clone?.path ?? [])],
      filters: cleanFilters(this.clone?.filters ?? this.filters),
    };
  }

  /** Someone else's centre screen; true when what it shows changed. */
  setState(state: { view?: unknown; path?: unknown; filters?: unknown }): boolean {
    const view = (typeof state.view === 'string' ? state.view : 'summary') as CentreView;
    const changed = view !== this.showing;
    if (changed) {
      this.show(view);
    }
    this.clone?.setView({ path: state.path, filters: state.filters ?? this.filters });
    return changed;
  }

  setFilters(f: DashboardFilters): void {
    this.filters = { ...f };
    this.summary.setFilters(f);
    this.clone?.setFilters(f);
  }

  /** Records changed (queries): the summary's numbers and the clone redraw. */
  refresh(): void {
    this.summary.refresh();
    this.clone?.invalidate();
  }

  /** The screen stands only in the arc layout; the presets move every screen elsewhere. */
  setVisible(visible: boolean): void {
    if (visible === this.visible) return;
    this.visible = visible;
    const current = this.clone ?? this.summary;
    const mesh = this.clone ? this.clone.group : this.summary.panel.mesh;
    mesh.visible = visible;
    if (current === this.clone) {
      if (visible) this.clone.attach();
      else this.clone.detach();
    } else if (visible) this.summary.panel.attach(this.host.interactive);
    else this.summary.panel.detach(this.host.interactive);
  }

  get isVisible(): boolean {
    return this.visible;
  }

  /** The live canvas of what is on screen (the console draws it as the active thumbnail). */
  canvas(): HTMLCanvasElement {
    return (this.clone?.panel ?? this.summary.panel).canvas;
  }

  update(): void {
    // The summary keeps drawing while hidden: the console shows it as a thumbnail.
    this.summary.update();
    if (this.visible) this.clone?.update();
  }

  private dropClone(): void {
    if (!this.clone) return;
    this.group.remove(this.clone.group);
    this.clone.dispose();
    this.clone = undefined;
  }

  dispose(): void {
    this.dropClone();
    this.summary.panel.detach(this.host.interactive);
    this.summary.dispose();
  }
}
